import React from "react"
import { connect } from "react-redux";
import { withRouter } from 'react-router-dom';
import { Button, Input, Spin, Upload, message as antMessage, Avatar } from 'antd';
import {
    ArrowLeftOutlined, UserAddOutlined, CloseCircleOutlined,
    PaperClipOutlined, SendOutlined, CloseOutlined,
    FileImageOutlined, FilePdfOutlined, FileTextOutlined
} from '@ant-design/icons';
import { fetchTicketDetail, fetchTicketCauses, createTicketMessage, updateTicketConversation, getTicketAttachmentUrl } from '../../actions/actions.js';
import moment from 'moment-business-days';
import './ticketConversation.css';

const { TextArea } = Input;

/* L'API espone solo tcIsOpen (booleano): niente stati intermedi come "in lavorazione"/"risolto" */
function ticketStatus(ticket) {
    return ticket.tcIsOpen ? { label: 'Aperto', cls: 'sp-open' } : { label: 'Chiuso', cls: 'sp-closed' };
}

function formatDate(value) {
    if (!value) return '-';
    return moment(value, 'YYYY-MM-DD HH:mm:ss.S').format('DD/MM/YYYY HH:mm');
}

function formatCustomer(ticket) {
    if (ticket.tcCustomerUsername) return ticket.tcCustomerId + ' - ' + ticket.tcCustomerUsername;
    return 'Cliente #' + ticket.tcCustomerId;
}

function formatOperator(ticket) {
    if (ticket.tcOperatorUsername) return ticket.tcOperatorUsername;
    if (ticket.tcOperatorId) return 'Operatore #' + ticket.tcOperatorId;
    return 'Non assegnato';
}

function resolveCauseName(causeId, causes) {
    const found = (causes || []).find(c => c.caId === causeId);
    if (found) return found.caDescription;
    return 'Causale #' + causeId;
}

/* Il messaggio riporta solo l'id numerico del mittente (tmSenderId), non un nome: mostriamo un'etichetta generica per ruolo */
function messageSenderLabel(msg) {
    /*return (msg.urole === 'ADMIN' ? 'Operatore #' : 'Cliente #') + msg.tmSenderId*/
    return msg.tmSenderUsername;
}

function messageInitials(msg) {
    return msg.tmSenderUsername.slice(0, 2).toUpperCase();
}

/* Il ticket viene caricato come pagina: l'id è l'ultimo segmento dell'URL (vedi PromoDetail) */
function getIdFromUrl() {
    let url = new URL(window.location.href);
    let split = url.pathname.split('/');
    return split[split.length - 1];
}

const mapStateToProps = state => {
    return {
        ticketDetail: state.ticketDetail,
        ticketCauses: state.ticketCauses,
        ticketMessageCreate: state.ticketMessageCreate,
        ticketUpdate: state.ticketUpdate,
        user: state.user
    };
};

function mapDispatchToProps(dispatch) {
    return {
        fetchTicketDetail: (id) => dispatch(fetchTicketDetail(id)),
        fetchTicketCauses: () => dispatch(fetchTicketCauses()),
        createTicketMessage: (id, body, attachments) => dispatch(createTicketMessage(id, body, attachments)),
        updateTicketConversation: (id, params) => dispatch(updateTicketConversation(id, params))
    };
}

class TicketConversation extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            ticketId: props.ticketId !== undefined ? props.ticketId : getIdFromUrl(),
            ticketDetail: this.props.ticketDetail,
            draftText: '',
            pendingFiles: []
        }

        this.handleBack = this.handleBack.bind(this);
        this.handleSend = this.handleSend.bind(this);
        this.handleUpload = this.handleUpload.bind(this);
        this.handleRemoveFile = this.handleRemoveFile.bind(this);
        this.handleCloseTicket = this.handleCloseTicket.bind(this);
        this.handleTakeCharge = this.handleTakeCharge.bind(this);
        this.isOperator = this.isOperator.bind(this);
        this.chatScrollRef = React.createRef();
    }

    componentDidMount() {
        this.props.fetchTicketDetail(this.state.ticketId);
        this.props.fetchTicketCauses();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.ticketDetail !== prevProps.ticketDetail) {
            this.setState({
                ...this.state,
                ticketDetail: this.props.ticketDetail
            });
            /* Aspetta che la lista messaggi aggiornata sia nel DOM prima di scrollare in fondo */
            setTimeout(() => this.scrollToBottom(), 50);
        }
    }

    scrollToBottom() {
        const el = this.chatScrollRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }

    isOperator() {
        return this.props.user.uRole === 'ADMIN';
    }

    handleBack() {
        this.props.history.push('/ticketList');
    }

    handleUpload(file) {
        this.setState({
            ...this.state,
            pendingFiles: [...this.state.pendingFiles, file]
        });
        return false;
    }

    handleRemoveFile(index) {
        this.setState({
            ...this.state,
            pendingFiles: this.state.pendingFiles.filter((f, i) => i !== index)
        });
    }

    handleCloseTicket() {
        this.props.updateTicketConversation(this.state.ticketId, { isOpen: false }).then((updated) => {
            if (updated) {
                this.props.fetchTicketDetail(this.state.ticketId);
            }
        });
    }

    handleTakeCharge() {
        this.props.updateTicketConversation(this.state.ticketId, { assignToCurrentOperator: true }).then((updated) => {
            if (updated) {
                this.props.fetchTicketDetail(this.state.ticketId);
            }
        });
    }

    handleSend() {
        const body = this.state.draftText.trim();
        if (!body && this.state.pendingFiles.length === 0) {
            return;
        }

        const data = this.state.ticketDetail.data;
        if (this.isOperator() && data && !data.tcOperatorId) {
            antMessage.error("Devi prima prendere in carico il ticket prima di poter rispondere.");
            return;
        }

        this.props.createTicketMessage(this.state.ticketId, body, this.state.pendingFiles).then((created) => {
            if (created) {
                this.setState({
                    ...this.state,
                    draftText: '',
                    pendingFiles: []
                });
                this.props.fetchTicketDetail(this.state.ticketId);
            }
        });
    }

    renderAttachment(att, isYou) {
        /* Tipi consentiti: .jpeg, .png, .webp, .heic, .heif (image/*) e .pdf (application/pdf) */
        const mimeType = att.taMimeType || '';
        let Icon = FileTextOutlined;
        if (mimeType.indexOf('image/') === 0) {
            Icon = FileImageOutlined;
        } else if (mimeType === 'application/pdf') {
            Icon = FilePdfOutlined;
        }
        return (
            <a
                className={"att-card " + (isYou ? "you" : "other")}
                key={att.taId}
                href={getTicketAttachmentUrl(att.taUuid)}
                target="_blank"
                rel="noopener noreferrer"
            >
                <div className="att-ico"><Icon /></div>
                <div className="att-info">
                    <div className="att-name">Allegato: {att.taFilename}</div>
                </div>
            </a>
        );
    }

    renderMessage(msg, operator) {
        const isYou = operator ? msg.urole === 'ADMIN' : msg.urole === 'USER';
        return (
            <div className={"msg-row " + (isYou ? "you" : "other")} key={msg.tmId}>
                <Avatar className={"av " + (isYou ? "av-you" : "av-other")}>{messageInitials(msg)}</Avatar>
                <div className="msg-wrap">
                    <div className="msg-meta">
                        {<span className="sender">{messageSenderLabel(msg)}</span>}
                        <span>{formatDate(msg.tmCreatedOn)}</span>
                    </div>
                    <div className={"bubble " + (isYou ? "you" : "other")}>{msg.tmBody}</div>
                    {(msg.attachments || []).map((att) => this.renderAttachment(att, isYou))}
                </div>
            </div>
        );
    }

    render() {

        const data = this.state.ticketDetail.data;
        const operator = this.isOperator();
        const causes = this.props.ticketCauses.data || [];

        if (this.state.ticketDetail.requestIsPending || !data) {
            return (
                <div>
                    <Spin style={{ marginLeft: "49%", paddingTop: "2em" }} size="large" tip="Caricamento ticket in corso..." />
                </div>
            )
        }

        const status = ticketStatus(data);
        const sendBlocked = operator && !data.tcOperatorId;

        return (
            <div className="ticket-conversation-view">
                <div className="detail-topbar">
                    <Button className="back-btn" icon={<ArrowLeftOutlined />} onClick={this.handleBack}>
                        Torna alla lista
                    </Button>
                    <span className="detail-ticket-id">#{data.tcId}</span>
                    <span className="detail-ticket-title">{data.tcSubject}</span>
                    <span className={"sp " + status.cls}>{status.label}</span>

                    <div className="topbar-actions">
                        <Button
                            danger
                            disabled={!data.tcIsOpen}
                            loading={this.props.ticketUpdate.requestIsPending}
                            onClick={this.handleCloseTicket}
                        >
                            <CloseCircleOutlined /> Chiudi ticket
                        </Button>
                        {operator && (
                            <React.Fragment>
                                {data.tcOperatorId ? (
                                    <Button type="primary" disabled>
                                        <UserAddOutlined /> Preso in carico
                                    </Button>
                                ) : (
                                    <Button className="action-btn" loading={this.props.ticketUpdate.requestIsPending} onClick={this.handleTakeCharge}>
                                        <UserAddOutlined /> Prendi in carico
                                    </Button>
                                )}
                            </React.Fragment>
                        )}
                    </div>
                </div>

                <div className="detail-infobar">
                    <span className="info-item">Aperto il <strong>{formatDate(data.tcOpenedOn)}</strong></span>
                    <span className="info-item">Cliente: <strong>{formatCustomer(data)}</strong></span>
                    <span className="info-item">Operatore: <strong>{formatOperator(data)}</strong></span>
                    <span className="info-item">Causale: <strong>{resolveCauseName(data.tcCauseId, causes)}</strong></span>
                </div>

                <div className="chat-scroll" ref={this.chatScrollRef}>
                    {(data.tcMessages || []).map((msg) => this.renderMessage(msg, operator))}
                </div>

                <div className="input-area-wrap">
                    {sendBlocked && (
                        <div className="send-blocked-hint">Prendi in carico il ticket prima di poter rispondere.</div>
                    )}
                    <div className="input-inner">
                        <div className="input-field-wrap">
                            <TextArea
                                className="chat-input"
                                rows={2}
                                placeholder="Scrivi un messaggio…"
                                value={this.state.draftText}
                                onChange={(e) => this.setState({ ...this.state, draftText: e.target.value })}
                            />
                            <div className="input-toolbar">
                                <Upload beforeUpload={this.handleUpload} multiple showUploadList={false}>
                                    <Button className="attach-pill" icon={<PaperClipOutlined />}>
                                        Allega file
                                    </Button>
                                </Upload>
                                {this.state.pendingFiles.map((file, idx) => (
                                    <span className="pending-attachment-chip" key={file.name + idx}>
                                        {file.name}
                                        <CloseOutlined onClick={() => this.handleRemoveFile(idx)} />
                                    </span>
                                ))}
                            </div>
                        </div>
                        <Button
                            type="primary"
                            className="send-btn"
                            icon={<SendOutlined />}
                            loading={this.props.ticketMessageCreate.requestIsPending}
                            disabled={sendBlocked}
                            onClick={this.handleSend}
                        >
                            Invia
                        </Button>
                    </div>
                </div>
            </div>
        )
    }
}


export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TicketConversation));
